import type { MatrixClient, MatrixEvent, User } from 'matrix-js-sdk';
import { ClientEvent } from 'matrix-js-sdk';

type Listener = () => void;

export type MatrixUserState = {
  user: User | null;
  userId: string;
  displayName: string | null;
  avatarUrl: string | null;
  isCurrentlyActive: boolean | null;
  lastActiveAgo: number | null;
  presence: string | null;
};

/**
 * Store für einen spezifischen Matrix User (nicht den eigenen).
 * Trackt CurrentlyActive Status und andere User-Properties.
 *
 * Nutzt globale Client-Events (User.presence, User.displayName) statt User-spezifische Listener.
 */
class MatrixUserStoreClass {
  private listeners = new Map<string, Set<Listener>>();
  private client: MatrixClient | null = null;
  private states = new Map<string, MatrixUserState>();
  private pendingUpdates = new Set<string>();
  private globalHandlersBound = false;

  /**
   * Setzt den Matrix Client.
   */
  setClient(client: MatrixClient | null): void {
    // Cleanup old client
    if (this.client && this.globalHandlersBound) {
      this.client.removeListener(ClientEvent.Event, this.handleGlobalEvent);
      this.globalHandlersBound = false;
    }

    this.client = client;

    // Setup global listeners
    if (this.client && !this.globalHandlersBound) {
      this.client.on(ClientEvent.Event, this.handleGlobalEvent);
      this.globalHandlersBound = true;

      // Re-initialize all tracked users
      for (const userId of this.listeners.keys()) {
        this.updateUserState(userId);
      }
    }
  }

  /**
   * Handler für globale Events.
   * Filtert nach m.presence Events für User-Updates.
   */
  private handleGlobalEvent = (event: MatrixEvent): void => {
    const type = event.getType();

    // m.presence events enthalten User-Status-Updates
    if (type === 'm.presence') {
      const userId = event.getSender();
      if (userId && this.listeners.has(userId)) {
        this.handleUserChange(userId);
      }
    }

    // Room member events können DisplayName/Avatar ändern
    if (type === 'm.room.member') {
      const userId = event.getStateKey();
      if (userId && this.listeners.has(userId)) {
        this.handleUserChange(userId);
      }
    }
  };

  /**
   * Handler für User-Änderungen.
   * Verwendet Microtask-Queue für effizientes Batching.
   */
  private handleUserChange = (userId: string): void => {
    if (this.pendingUpdates.has(userId)) return;

    this.pendingUpdates.add(userId);
    queueMicrotask(() => {
      this.pendingUpdates.delete(userId);

      const previousState = this.states.get(userId);
      this.updateUserState(userId);
      const newState = this.states.get(userId);

      // Nur emittieren wenn sich Status wirklich geändert hat
      if (
        !previousState ||
        previousState.displayName !== newState?.displayName ||
        previousState.avatarUrl !== newState?.avatarUrl ||
        previousState.isCurrentlyActive !== newState?.isCurrentlyActive ||
        previousState.lastActiveAgo !== newState?.lastActiveAgo ||
        previousState.presence !== newState?.presence
      ) {
        this.emitChange(userId);
      }
    });
  };

  /**
   * Aktualisiert den User-Status.
   * Erstellt nur ein neues State-Objekt wenn sich wirklich etwas geändert hat.
   */
  private updateUserState(userId: string): void {
    const existingState = this.states.get(userId);

    if (!this.client) {
      const newState: MatrixUserState = {
        user: null,
        userId: userId,
        displayName: null,
        avatarUrl: null,
        isCurrentlyActive: null,
        lastActiveAgo: null,
        presence: null,
      };

      // Nur setzen wenn sich etwas geändert hat
      if (!existingState || !this.statesEqual(existingState, newState)) {
        this.states.set(userId, newState);
      }
      return;
    }

    const user = this.client.getUser(userId);

    if (!user) {
      const newState: MatrixUserState = {
        user: null,
        userId: userId,
        displayName: null,
        avatarUrl: null,
        isCurrentlyActive: null,
        lastActiveAgo: null,
        presence: null,
      };

      // Nur setzen wenn sich etwas geändert hat
      if (!existingState || !this.statesEqual(existingState, newState)) {
        this.states.set(userId, newState);
      }
      return;
    }

    const newState: MatrixUserState = {
      user: user,
      userId: userId,
      displayName: user.displayName ?? null,
      avatarUrl: user.avatarUrl ?? null,
      isCurrentlyActive: user.currentlyActive ?? null,
      lastActiveAgo: user.lastActiveAgo ?? null,
      presence: user.presence ?? null,
    };

    // Nur setzen wenn sich etwas geändert hat
    if (!existingState || !this.statesEqual(existingState, newState)) {
      this.states.set(userId, newState);
    }
  }

  /**
   * Vergleicht zwei States auf Gleichheit (ohne User-Objekt).
   */
  private statesEqual(a: MatrixUserState, b: MatrixUserState): boolean {
    return (
      a.userId === b.userId &&
      a.displayName === b.displayName &&
      a.avatarUrl === b.avatarUrl &&
      a.isCurrentlyActive === b.isCurrentlyActive &&
      a.lastActiveAgo === b.lastActiveAgo &&
      a.presence === b.presence
    );
  }

  /**
   * Gibt den aktuellen Snapshot für einen User zurück.
   */
  getSnapshot = (userId: string): MatrixUserState => {
    let state = this.states.get(userId);

    if (!state) {
      // Initial state erstellen
      this.updateUserState(userId);
      state = this.states.get(userId);
    }

    return (
      state ?? {
        user: null,
        userId: userId,
        displayName: null,
        avatarUrl: null,
        isCurrentlyActive: null,
        lastActiveAgo: null,
        presence: null,
      }
    );
  };

  /**
   * Subscribe-Funktion für useSyncExternalStore.
   */
  subscribe = (userId: string, listener: Listener): (() => void) => {
    // Get or create listener set for this user
    let userListeners = this.listeners.get(userId);
    if (!userListeners) {
      userListeners = new Set();
      this.listeners.set(userId, userListeners);

      // Initial state laden
      if (this.client) {
        this.updateUserState(userId);
      }
    }

    userListeners.add(listener);

    return () => {
      userListeners?.delete(listener);

      // Cleanup wenn keine Listener mehr für diesen User
      if (userListeners?.size === 0) {
        this.listeners.delete(userId);
        this.states.delete(userId);
      }
    };
  };

  /**
   * Benachrichtigt alle Listener für einen User über Änderungen.
   */
  private emitChange(userId: string): void {
    const userListeners = this.listeners.get(userId);
    if (!userListeners) return;

    for (const listener of userListeners) {
      listener();
    }
  }

  /**
   * Gibt den aktuellen Client zurück.
   */
  getClient(): MatrixClient | null {
    return this.client;
  }
}

// Singleton-Instanz
export const matrixUserStore = new MatrixUserStoreClass();
